// @file For detecting mobile notch
import device from '@@/bits/device'
import { useSurfaceStore } from '@@/pinia/surface'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useSurfaceWindowMobileNotchStore = defineStore('surfaceWindowMobileNotch', () => {
  const surfaceStore = useSurfaceStore()
  const safeAreaInsetLeft = ref(0)
  const safeAreaInsetRight = ref(0)
  const safeAreaInsetBottom = ref(0)

  function updateSafeAreaInset(): void {
    try {
      safeAreaInsetLeft.value =
        parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-left').replace('px', ''),
          10,
        ) ?? 0

      safeAreaInsetRight.value =
        parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-right').replace('px', ''),
          10,
        ) ?? 0
      safeAreaInsetBottom.value =
        parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom').replace('px', ''),
          10,
        ) ?? 0
    } catch (e) {
      // Ignore if --safe-area-inset-* is not set in :root stylesheets
    }
  }

  const isLandscapeNotchLeft = ref(false)
  const isLandscapeNotchRight = ref(false)

  const isLandscapeNotchStart = computed(() => {
    return surfaceStore.dir === 'ltr' ? isLandscapeNotchLeft.value : isLandscapeNotchRight.value
  })

  const isLandscapeNotchEnd = computed(() => {
    return surfaceStore.dir === 'ltr' ? isLandscapeNotchRight.value : isLandscapeNotchLeft.value
  })

  function updateLandscapeNotch(): void {
    // Some older browsers do not support screen.orientation
    if (screen.orientation === undefined) return
    switch (screen.orientation.type) {
      case 'landscape-primary':
        isLandscapeNotchLeft.value = true
        isLandscapeNotchRight.value = false
        break
      case 'landscape-secondary':
        isLandscapeNotchLeft.value = false
        isLandscapeNotchRight.value = true
        break
      default:
        isLandscapeNotchLeft.value = false
        isLandscapeNotchRight.value = false
        break
    }
  }

  const safeAreaInsetStart = computed(() => {
    // LTR + Notch on the left
    if (surfaceStore.dir === 'ltr' && isLandscapeNotchLeft.value) {
      return safeAreaInsetLeft.value
    }
    // RTL + Notch on the right
    if (surfaceStore.dir === 'rtl' && isLandscapeNotchRight.value) {
      return safeAreaInsetRight.value
    }
    return 0
  })

  const safeAreaInsetEnd = computed(() => {
    // LTR + Notch on the right
    if (surfaceStore.dir === 'ltr' && isLandscapeNotchRight.value) {
      return safeAreaInsetRight.value
    }
    // RTL + Notch on the left
    if (surfaceStore.dir === 'rtl' && isLandscapeNotchLeft.value) {
      return safeAreaInsetLeft.value
    }
    return 0
  })

  const isApplyingSafeAreaInsetStart = computed(
    () =>
      safeAreaInsetStart.value > 0 ||
      ((isLandscapeNotchLeft.value || (isLandscapeNotchRight.value && surfaceStore.dir === 'rtl')) && device.app),
  )

  const isApplyingSafeAreaInsetEnd = computed(
    () =>
      safeAreaInsetEnd.value > 0 ||
      ((isLandscapeNotchRight.value || (isLandscapeNotchLeft.value && surfaceStore.dir === 'rtl')) && device.app),
  )

  return {
    safeAreaInsetLeft,
    safeAreaInsetRight,
    safeAreaInsetStart,
    safeAreaInsetEnd,
    safeAreaInsetBottom,
    isLandscapeNotchLeft,
    isLandscapeNotchRight,
    isLandscapeNotchStart,
    isLandscapeNotchEnd,
    isApplyingSafeAreaInsetStart,
    isApplyingSafeAreaInsetEnd,
    updateSafeAreaInset,
    updateLandscapeNotch,
  }
})
